'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Stack } from '@mui/material';
import { isFalsy } from 'utility-types';
import { useAppStoreStore } from '@/stores/appstore.store';
import { useFavoriteStore } from '@/stores/favoriteStore.store';
import { REVIEW_ROUTES } from '@/constants/path.constants';
import { getToken } from '@/utils/auth.util';
import EmptyCompnent from '@/components/EmptyComponent';
import Footer from '@/components/footer/Footer';
import AppStoreDashboardCaseResolver from '@/views/appstore/dashboard/AppStoreDashboardCaseResolver';
import BannerEnterprise from '@/views/appstore/dashboard/components/banner';
import { DESKTOP_CONTENTS_MAX_WIDTH, DESKTOP_HEADER_HEIGHT, MOBILE_HEADER_HEIGHT } from '@/app/stylesguides';
import useCheckIsMobileSize from '@/hooks/useCheckIsMobileSize';
import { Header } from '@/shared/ui/organisms';

const AppStoreDashboard = ({ appStoreId }: { appStoreId: number }) => {
  const router = useRouter();
  const [isCheckedAppStore, setIsCheckedAppStore] = useState(false);
  const { appStores } = useAppStoreStore((state) => state);
  const { getUserStoreId } = useFavoriteStore();
  const isMobile = useCheckIsMobileSize();

  useEffect(() => {
    const initializeDashboard = () => {
      // appStoreId가 유효한 경우
      if (appStoreId > -1) {
        // 이미 appStores에 해당 store가 있는지 확인
        if (appStores[appStoreId]) {
          setIsCheckedAppStore(true);
          return;
        }

        // store가 없는 경우 기본 대시보드로 리다이렉트
        router.replace(REVIEW_ROUTES.getStoreDashboard(-1));
        return;
      }

      // appStoreId가 유효하지 않은 경우 (기본 대시보드로 리다이렉트)
      const storeKeys = Object.keys(appStores);

      // 1. appStores에 저장된 store가 있는 경우
      if (storeKeys.length > 0) {
        router.replace(REVIEW_ROUTES.getStoreDashboard(Number(storeKeys[0])));
        return;
      }

      // 2. 로그인 상태이고 관심 병원이 있는 경우
      const token = getToken();
      const favoriteStoreId = getUserStoreId();
      if (!isFalsy(token?.accessToken) && !isFalsy(favoriteStoreId)) {
        router.replace(REVIEW_ROUTES.getStoreDashboard(Number(favoriteStoreId)));
        return;
      }

      // 3. 세션 스토리지에서 이전 store 정보 확인
      const legacyAppStore = sessionStorage.getItem('AppStoreStore');
      if (legacyAppStore) {
        try {
          const parsedLegacyAppStore = JSON.parse(legacyAppStore);
          const currentAppStore = parsedLegacyAppStore.state?.currentAppStore;

          if (currentAppStore?.storeId) {
            router.replace(REVIEW_ROUTES.getStoreDashboard(Number(currentAppStore.storeId)));
            return;
          }
        } catch (error) {
          console.error('세션 스토리지 파싱 오류:', error);
        }
      }

      // 4. 모든 조건이 실패한 경우 기본 경로로 리다이렉트
      router.replace(REVIEW_ROUTES.getStoreDashboard(-1));
      setIsCheckedAppStore(true);
    };

    initializeDashboard();
  }, [appStores, appStoreId, getUserStoreId, router]);

  if (!isCheckedAppStore) {
    return <EmptyCompnent />;
  }

  return (
    <>
      <Header />
      <Stack
        sx={{
          width: '100%',
          maxWidth: isMobile ? '100%' : `${DESKTOP_CONTENTS_MAX_WIDTH}px`,
          position: 'relative',
          minHeight: `calc(100vh - ${isMobile ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT}px)`,
          paddingTop: isMobile ? '30px' : '60px',
          paddingX: isMobile ? '16px' : 0,
          margin: 'auto',
          gap: isMobile ? 0 : '60px'
        }}
      >
        <Stack direction={'column'} gap={'16px'} flex={'1 0 0'} width={'100%'}>
          <AppStoreDashboardCaseResolver appStoreId={appStoreId} isMobile={isMobile} />
          {!isMobile && <BannerEnterprise />}
        </Stack>
        <Footer />
      </Stack>
    </>
  );
};

export default AppStoreDashboard;
